import type { Player } from "../../common/types.ts";
import { PHASE } from "../../common/constants.ts";
import assignPositionsSmart from "../util/assignPositionsSmart.ts";
import { getGlobalSettings } from "../util/getGlobalSettings.ts";
import { g, helpers } from "../util/index.ts";
import { idb } from "../db/index.ts";
import { season } from "../core/index.ts";

const formatHgtWgt = (
	hgt: number | undefined,
	weight: number | undefined,
	units: "metric" | "us",
) => {
	if (!hgt || !weight) {
		return { hgt: "", wgt: "" };
	}
	if (units === "us") {
		return {
			hgt: `${Math.floor(hgt / 12)}-${hgt % 12}`,
			wgt: `${Math.round(weight)}lbs`,
		};
	}
	return {
		hgt: `${Math.round(hgt * 2.54)}cm`,
		wgt: `${Math.round(weight / 2.205)}kg`,
	};
};

const statHeader =
	"Pos, Name, Hgt, Wgt, Age, Team, G, GS, MP | PTS, REB, AST, STL, BLK, PF, TOV | ORB, DRB, BA | FG, FGA, FG%, 3P, 3PA, 3P%, 2P, 2PA, 2P%, eFG%, FT, FTA, FT%";

const getRatings = (p: any) =>
	Array.isArray(p.ratings) ? (p.ratings.at(-1) ?? {}) : (p.ratings ?? {});
const getStats = (p: any) =>
	Array.isArray(p.stats) ? (p.stats.at(-1) ?? {}) : (p.stats ?? {});

const formatPlayerStats = (
	p: any,
	units: "metric" | "us",
	position?: string,
	isSixthMan = false,
	isInjured = false,
	teamAbbrev?: string,
	season?: number,
	perGameStats = false,
	includeHeightWeight = true,
) => {
	const ratings = getRatings(p);
	const s = getStats(p);
	const hw = formatHgtWgt(p.hgt, p.weight, units);
	const trb =
		s.trb !== undefined && s.trb > 0 ? s.trb : (s.orb ?? 0) + (s.drb ?? 0);
	const gp = s.gp ?? 1;
	const perGame = (value: number) => (gp > 0 ? value / gp : 0);
	const statValue = (value: number) => (perGameStats ? perGame(value) : value);
	const fgp = helpers.percentage(s.fg, s.fga) ?? 0;
	const tpp = helpers.percentage(s.tp, s.tpa) ?? 0;
	const twoPP =
		helpers.percentage(
			(s.fg ?? 0) - (s.tp ?? 0),
			(s.fga ?? 0) - (s.tpa ?? 0),
		) ?? 0;
	const efg = helpers.percentage((s.fg ?? 0) + 0.5 * (s.tp ?? 0), s.fga) ?? 0;
	const ftp = helpers.percentage(s.ft, s.fta) ?? 0;
	const suffix = isSixthMan
		? " --- SIXTH MAN"
		: isInjured
			? " --- Injured"
			: "";
	const age =
		season !== undefined && p.born?.year ? season - p.born.year : (p.age ?? "");
	const values = [
		position ?? ratings.pos ?? "",
		p.name ?? `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim(),
		...(includeHeightWeight ? [hw.hgt, hw.wgt] : []),
		age,
		teamAbbrev ?? p.abbrev ?? "",
		s.gp ?? 0,
		s.gs ?? 0,
		statValue(s.min ?? 0).toFixed(1) + " |",
		statValue(s.pts ?? 0).toFixed(1),
		statValue(trb).toFixed(1),
		statValue(s.ast ?? 0).toFixed(1),
		statValue(s.stl ?? 0).toFixed(1),
		statValue(s.blk ?? 0).toFixed(1),
		statValue(s.pf ?? 0).toFixed(1),
		statValue(s.tov ?? 0).toFixed(1) + " |",
		statValue(s.orb ?? 0).toFixed(1),
		statValue(s.drb ?? 0).toFixed(1),
		statValue(s.ba ?? 0).toFixed(1) + " |",
		statValue(s.fg ?? 0).toFixed(1),
		statValue(s.fga ?? 0).toFixed(1),
		fgp.toFixed(1),
		statValue(s.tp ?? 0).toFixed(1),
		statValue(s.tpa ?? 0).toFixed(1),
		tpp.toFixed(1),
		statValue((s.fg ?? 0) - (s.tp ?? 0)).toFixed(1),
		statValue((s.fga ?? 0) - (s.tpa ?? 0)).toFixed(1),
		twoPP.toFixed(1),
		efg.toFixed(1),
		statValue(s.ft ?? 0).toFixed(1),
		statValue(s.fta ?? 0).toFixed(1),
		ftp.toFixed(1) + " |",
	];
	return values.join(" ") + suffix;
};

const splitPlayers = (team: any, numStarters: number) => {
	const players = [...(team.players ?? [])].sort(
		(a, b) => (a.rosterOrder ?? 0) - (b.rosterOrder ?? 0),
	);
	return {
		starters: players.slice(0, numStarters),
		bench: players.slice(numStarters),
	};
};

const formatTeamSummary = ({
	team,
	units,
	season,
	numStarters,
	includeHeightWeight = true,
	perGameStats = false,
}: {
	team: any;
	units: "metric" | "us";
	season?: number;
	numStarters: number;
	includeHeightWeight?: boolean;
	perGameStats?: boolean;
}) => {
	const { starters, bench } = splitPlayers(team, numStarters);
	const assigned = assignPositionsSmart(starters);
	const record =
		team.seasonInfo && !team.custom
			? ` : ${team.seasonInfo.won}-${team.seasonInfo.lost}`
			: "";
	const label = `${team.region ? `${team.region} ` : ""}${team.name ?? ""}${team.season ? ` (${team.season})` : ""}`;
	let output =
		`${label}${record} ${includeHeightWeight ? statHeader : statHeader.replace("Hgt, Wgt, ", "")}` +
		"\n\nSTARTING LINEUP :\n";
	for (const pos of ["PG", "SG", "SF", "PF", "C"]) {
		const p = assigned[pos];
		if (p) {
			output += `${formatPlayerStats(p, units, pos, false, p.injury?.gamesRemaining > 0, team.abbrev, season, perGameStats, includeHeightWeight)}\n`;
		}
	}
	output += "\nBENCH\n";
	bench.forEach((p: any, i: number) => {
		output += `${formatPlayerStats(p, units, undefined, i === 0, p.injury?.gamesRemaining > 0, team.abbrev, season, perGameStats, includeHeightWeight)}\n`;
	});
	return output;
};

export const generateGameSummary = async (gid: number) => {
	const schedule = await season.getSchedule();
	const game = schedule.find((g) => g.gid === gid);
	if (!game) {
		return null;
	}
	const { units = "us" } = await getGlobalSettings();
	if (game.homeTid === -1 && game.awayTid === -2) {
		const allStars = await idb.cache.allStars.get(g.get("season"));
		if (!allStars) {
			return null;
		}
		const makeTeam = async (index: 0 | 1) => ({
			name: allStars.teamNames[index] ?? `All-Stars ${index + 1}`,
			players: await Promise.all(
				allStars.teams[index].map(async (playerInfo: any) => {
					return (await idb.cache.players.get(playerInfo.pid)) ?? playerInfo;
				}),
			),
		});
		const [home, away] = await Promise.all([makeTeam(0), makeTeam(1)]);
		return `${home.name}(Home) vs ${away.name}(Away) (All-Star Game)\n${formatTeamSummary({ team: home, units, numStarters: 5 })}${formatTeamSummary({ team: away, units, numStarters: 5 })}`;
	}
	const teams = await idb.getCopies.teamsPlus(
		{
			attrs: ["tid", "region", "name", "abbrev"],
			seasonAttrs: ["won", "lost"],
			season: g.get("season"),
			active: true,
		},
		"noCopyCache",
	);
	const homeTeam = teams.find((t) => t.tid === game.homeTid);
	const awayTeam = teams.find((t) => t.tid === game.awayTid);
	if (!homeTeam || !awayTeam) {
		return null;
	}
	let playoffSeriesText = "";
	if (g.get("phase") === PHASE.PLAYOFFS) {
		const playoffSeries = await idb.getCopy.playoffSeries({
			season: g.get("season"),
		});
		if (playoffSeries) {
			for (const [roundIndex, round] of playoffSeries.series.entries()) {
				const series = round.find(
					(s) =>
						(s.home.tid === game.homeTid && s.away?.tid === game.awayTid) ||
						(s.home.tid === game.awayTid && s.away?.tid === game.homeTid),
				);
				if (!series?.away) {continue;}
				const numRounds = playoffSeries.series.length;
				const roundName =
					roundIndex === numRounds - 1 && round.length === 1
						? "Finals"
						: `${g.get("confs")[series.home.cid]?.name ?? ""} Round ${roundIndex + 1}`;
				const homeWins = series.home.won;
				const awayWins = series.away.won;
				const homeAbbrev = series.home.abbrev ?? homeTeam.abbrev;
				const awayAbbrev = series.away.abbrev ?? awayTeam.abbrev;
				playoffSeriesText =
					homeWins === awayWins
						? ` (${roundName} - Series tied ${homeWins}-${awayWins})`
						: homeWins > awayWins
							? ` (${roundName} - ${homeAbbrev} leads ${homeWins}-${awayWins} against ${awayAbbrev})`
							: ` (${roundName} - ${awayAbbrev} leads ${awayWins}-${homeWins} against ${homeAbbrev})`;
				break;
			}
		}
	}
	const loadTeam = async (tid: number) => {
		const raw = await idb.cache.players.indexGetAll("playersByTid", tid);
		const players = await idb.getCopies.playersPlus(raw, {
			attrs: [
				"pid",
				"name",
				"tid",
				"rosterOrder",
				"injury",
				"abbrev",
				"age",
				"hgt",
				"weight",
				"born",
			],
			ratings: ["ovr", "pos", "skills"],
			stats: [
				"gp",
				"gs",
				"min",
				"fg",
				"fga",
				"tp",
				"tpa",
				"ft",
				"fta",
				"orb",
				"drb",
				"trb",
				"ast",
				"tov",
				"stl",
				"blk",
				"ba",
				"pf",
				"pts",
			],
			season: g.get("season"),
			showNoStats: true,
			showRookies: true,
			fuzz: true,
			tid,
		});
		return { players };
	};
	const [home, away] = await Promise.all([
		loadTeam(homeTeam.tid),
		loadTeam(awayTeam.tid),
	]);
	const numStarters = Math.max(5, g.get("numPlayersOnCourt") ?? 5);
	const homeWithPlayers = { ...homeTeam, ...home };
	const awayWithPlayers = { ...awayTeam, ...away };
	return `${homeTeam.region} ${homeTeam.name}(Home) vs ${awayTeam.region} ${awayTeam.name}(Away)${playoffSeriesText}\n${formatTeamSummary({ team: homeWithPlayers, units, season: g.get("season"), numStarters })}${formatTeamSummary({ team: awayWithPlayers, units, season: g.get("season"), numStarters })}`;
};

export const generateExhibitionMatchupSummary = async ({
	teams,
	neutralSite,
}: {
	teams: any[];
	neutralSite: boolean;
	playoffIntensity: boolean;
}) => {
	if (!teams || teams.length < 2) {
		return null;
	}
	const { units = "us" } = await getGlobalSettings();
	const home = teams[1];
	const away = teams[0];
	const header = `${home.region ? `${home.region} ` : ""}${home.name}${home.season ? ` (${home.season})` : ""} (Home) vs ${away.region ? `${away.region} ` : ""}${away.name}${away.season ? ` (${away.season})` : ""} (Away) ${neutralSite ? "(Neutral Court) " : ""}(Exhibition Game)`;
	return `${header}\n${formatTeamSummary({ team: home, units, season: home.season, numStarters: 5, perGameStats: true })}\n${formatTeamSummary({ team: away, units, season: away.season, numStarters: 5, perGameStats: true })}`;
};

export const generateExhibitionGameSummary = (boxScore: any) => {
	const teams = boxScore?.teams;
	if (!teams || teams.length < 2) {
		return null;
	}
	const [home, away] = teams;
	const homeLabel = `${home.region ? `${home.region} ` : ""}${home.name ?? ""}${home.season ? ` (${home.season})` : ""}`;
	const awayLabel = `${away.region ? `${away.region} ` : ""}${away.name ?? ""}${away.season ? ` (${away.season})` : ""}`;
	const { starters: homeStarters, bench: homeBench } = splitPlayers(
		home,
		Math.max(5, boxScore.numPlayersOnCourt ?? 5),
	);
	const { starters: awayStarters, bench: awayBench } = splitPlayers(
		away,
		Math.max(5, boxScore.numPlayersOnCourt ?? 5),
	);
	const formatBoxTeam = (
		label: string,
		team: any,
		starters: any[],
		bench: any[],
	) => {
		const assigned = assignPositionsSmart(starters);
		let output = `${label}${team.seasonInfo && !team.custom ? ` : ${team.seasonInfo.won}-${team.seasonInfo.lost}` : ""} ${statHeader.replace("Hgt, Wgt, ", "")}\n\nSTARTING LINEUP :\n`;
		for (const pos of ["PG", "SG", "SF", "PF", "C"]) {
			if (assigned[pos]) {
				output += `${formatPlayerStats(assigned[pos], "us", pos, false, false, undefined, undefined, false, false)}\n`;
			}
		}
		output += "\nBENCH\n";
		bench.forEach((p, i) => {
			output += `${formatPlayerStats(p, "us", undefined, i === 0, false, undefined, undefined, false, false)}\n`;
		});
		return output;
	};
	return `${homeLabel} (Home) vs ${awayLabel} (Away) ${boxScore.neutralSite ? "(Neutral Court) " : ""}(Exhibition Game)\n${formatBoxTeam(homeLabel, home, homeStarters, homeBench)}\n${formatBoxTeam(awayLabel, away, awayStarters, awayBench)}`;
};

export type SummaryPlayer = Player;
